import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, InputBase, Rating } from '@mui/material';
import {
  YardRounded, SpaRounded, SmartphoneRounded, CheckroomRounded, DiamondRounded, HourglassTopRounded,
  LocalShippingRounded, VerifiedUserRounded, AutorenewRounded, SupportAgentRounded, WorkspacePremiumRounded,
  SearchRounded, ShoppingBagOutlined, PersonOutlineRounded, FavoriteBorderRounded, PlayCircleOutlineRounded,
  FormatQuoteRounded, ArrowForwardRounded, MailOutlineRounded, PhoneRounded,
  BrushRounded, AutoAwesomeRounded, LocalOfferRounded, EmojiNatureRounded, PetsRounded, HandymanRounded,
  CameraAltRounded, HeadphonesRounded, BoltRounded, Instagram, Facebook, X, LinkedIn, YouTube,
} from '@mui/icons-material';

// Icons referenced by name from the template data in templates.js.
const ICONS = {
  plant: YardRounded, organic: SpaRounded, mobile: SmartphoneRounded, stylist: CheckroomRounded,
  jewelry: DiamondRounded, soon: HourglassTopRounded, shipping: LocalShippingRounded, secure: VerifiedUserRounded,
  returns: AutorenewRounded, support: SupportAgentRounded, quality: WorkspacePremiumRounded, brush: BrushRounded,
  sparkle: AutoAwesomeRounded, offer: LocalOfferRounded, nature: EmojiNatureRounded, pets: PetsRounded,
  tools: HandymanRounded, camera: CameraAltRounded, audio: HeadphonesRounded, bolt: BoltRounded,
};
const SOCIALS = [[Instagram, 'Instagram'], [Facebook, 'Facebook'], [X, 'X'], [LinkedIn, 'LinkedIn'], [YouTube, 'YouTube']];

// One-page storefront assembled from a list of sections. Each Shop template
// (Plant, Organic, Mobile, Stylist, Coming Soon, Jewelry) lists its sections in
// the order of the Phlox demo it is modelled on, with original text and
// stand-in artwork instead of product photos.
const ShopSite = ({ template, onAction, preview = false }) => {
  const { primary, accent, bg } = template.colors;
  const shop = template.shop;
  const act = onAction || (() => {});
  const sid = (id) => (preview ? undefined : id);
  const action = (href) => (preview ? { component: 'span' } : href ? { href } : { onClick: act });
  const link = (id) => (preview ? { component: 'span' } : { component: 'a', href: `#${id}` });
  const font = shop.serif ? '"Playfair Display", Georgia, serif' : '"Poppins", "Inter", sans-serif';
  const MainIcon = ICONS[shop.icon] || ShoppingBagOutlined;

  const inner = { maxWidth: 1180, mx: 'auto' };
  const section = { px: { xs: 3, md: 8 }, py: { xs: 6, md: 9 }, scrollMarginTop: 60 };
  const eyebrow = { color: accent, fontWeight: 600, letterSpacing: 2.5, textTransform: 'uppercase', fontSize: 13, mb: 1.25, fontFamily: '"Poppins", sans-serif' };
  const h2 = { fontFamily: font, fontWeight: shop.serif ? 600 : 800, fontSize: { xs: 28, md: 40 }, lineHeight: 1.15, letterSpacing: shop.serif ? 0 : '-0.02em' };
  const solid = { bgcolor: accent, color: '#fff', px: 3.5, py: 1.25, borderRadius: shop.round ?? 999, '&:hover': { bgcolor: accent, opacity: 0.9 } };
  const ghost = { color: 'inherit', border: '1.5px solid currentColor', px: 3.5, py: 1.25, borderRadius: shop.round ?? 999, opacity: 0.85 };

  // Stand-in artwork: a soft gradient tile with the template's icon.
  const Art = ({ i = 0, height, icon, dark, sx }) => {
    const Icon = ICONS[icon] || MainIcon;
    return (
      <Box sx={{
        height, borderRadius: shop.round === 0 ? 0 : 4, display: 'grid', placeItems: 'center', position: 'relative', overflow: 'hidden',
        background: dark
          ? `linear-gradient(${140 + i * 37}deg, ${accent}66 0%, ${primary} 100%)`
          : `linear-gradient(${140 + i * 37}deg, ${accent}38 0%, ${primary}14 100%)`,
        ...sx,
      }}>
        <Icon sx={{ fontSize: typeof height === 'number' ? Math.min(110, height / 2.4) : 72, color: dark ? '#ffffff66' : `${primary}55` }} />
      </Box>
    );
  };

  const Heading = ({ s, center = true }) => (
    <Box sx={{ textAlign: center ? 'center' : 'left', mb: { xs: 4, md: 5 } }}>
      {s.eyebrow && <Typography sx={eyebrow}>{s.eyebrow}</Typography>}
      <Typography component="h2" sx={h2}>{s.title}</Typography>
      {s.subtitle && <Typography sx={{ opacity: 0.65, mt: 1.25, maxWidth: 560, mx: center ? 'auto' : 0 }}>{s.subtitle}</Typography>}
    </Box>
  );

  const Price = ({ price, was }) => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', justifyContent: 'inherit' }}>
      <Typography sx={{ fontWeight: 700, color: accent }}>{price}</Typography>
      {was && <Typography sx={{ fontSize: 13, opacity: 0.45, textDecoration: 'line-through' }}>{was}</Typography>}
    </Box>
  );

  const ProductCard = ({ item, i, tall }) => (
    <Box sx={{ bgcolor: '#fff', borderRadius: shop.round === 0 ? 0 : 4, p: 1.25, boxShadow: `0 8px 28px ${primary}0d`, position: 'relative' }}>
      {item.badge && (
        <Box sx={{ position: 'absolute', top: 18, left: 18, zIndex: 1, bgcolor: primary, color: '#fff', fontSize: 11.5, fontWeight: 700, px: 1.25, py: 0.4, borderRadius: 999, fontFamily: '"Poppins", sans-serif' }}>
          {item.badge}
        </Box>
      )}
      {shop.cardRatio
        ? <Art i={i + 1} height="auto" icon={item.icon} sx={{ aspectRatio: shop.cardRatio }} />
        : <Art i={i + 1} height={tall ? 260 : 200} icon={item.icon} />}
      <Box sx={{ px: 1, pt: 2, pb: 1, textAlign: 'center', fontFamily: '"Poppins", sans-serif' }}>
        {item.tag && <Typography sx={{ fontSize: 12, opacity: 0.5, mb: 0.25 }}>{item.tag}</Typography>}
        <Typography sx={{ fontWeight: 600, fontSize: 15.5, mb: 0.75, fontFamily: font }}>{item.name}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}><Price price={item.price} was={item.was} /></Box>
        <Button size="small" {...action()} sx={{ mt: 1.5, px: 2.5, color: primary, border: `1px solid ${primary}22`, borderRadius: 999, fontSize: 12.5 }}>
          Add to cart
        </Button>
      </Box>
    </Box>
  );

  const Newsletter = ({ s, dark }) => (
    <Box sx={{ display: 'flex', gap: 1, maxWidth: 460, mx: s.center === false ? 0 : 'auto', bgcolor: dark ? '#ffffff14' : '#fff', p: 0.75, borderRadius: 999, border: `1px solid ${dark ? '#ffffff26' : `${primary}1a`}` }}>
      <InputBase placeholder="Your email address" inputProps={{ 'aria-label': 'Email address', tabIndex: preview ? -1 : 0 }}
        sx={{ flex: 1, px: 2, color: 'inherit', fontSize: 14.5, fontFamily: '"Poppins", sans-serif' }} />
      <Button {...action()} sx={{ ...solid, py: 1, px: 3, whiteSpace: 'nowrap' }}>{s.cta || 'Subscribe'}</Button>
    </Box>
  );

  const render = (s, idx) => {
    const key = `${s.type}-${idx}`;
    switch (s.type) {
      case 'topbar':
        return (
          <Box key={key} sx={{ bgcolor: primary, color: '#fff', px: { xs: 3, md: 8 }, py: 1, fontSize: 13, fontFamily: '"Poppins", sans-serif' }}>
            <Box sx={{ ...inner, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: 0.85 }}><PhoneRounded sx={{ fontSize: 16 }} />{s.text}</Box>
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1.5, opacity: 0.85 }}>
                {SOCIALS.slice(0, 4).map(([Icon, label]) => <Icon key={label} sx={{ fontSize: 17 }} />)}
              </Box>
            </Box>
          </Box>
        );

      case 'nav':
        return (
          <Box key={key} sx={{ px: { xs: 3, md: 8 }, py: 2.25, bgcolor: s.dark ? primary : bg, color: s.dark ? '#fff' : primary, borderBottom: `1px solid ${primary}10`, fontFamily: '"Poppins", sans-serif' }}>
            <Box sx={{ ...inner, display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                <MainIcon sx={{ color: accent, fontSize: 28 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography noWrap sx={{ fontWeight: 800, fontSize: 20, fontFamily: font, lineHeight: 1.1 }}>{shop.brand}</Typography>
                  {s.subtitle && <Typography noWrap sx={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.55 }}>{s.subtitle}</Typography>}
                </Box>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3.5 }}>
                {s.links.map(([label, id]) => (
                  <Typography key={label} {...link(id)} sx={{ color: 'inherit', textDecoration: 'none', fontSize: 14, fontWeight: 500, opacity: 0.8, '&:hover': { opacity: 1 } }}>
                    {label}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                {s.search !== false && <SearchRounded sx={{ fontSize: 22, display: { xs: 'none', sm: 'block' } }} />}
                {s.account && <PersonOutlineRounded sx={{ fontSize: 22, display: { xs: 'none', sm: 'block' } }} />}
                {s.wishlist && <FavoriteBorderRounded sx={{ fontSize: 21, display: { xs: 'none', sm: 'block' } }} />}
                {s.cta ? (
                  <Button {...action()} size="small" sx={{ ...solid, py: 0.75, px: 2.5 }}>{s.cta}</Button>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ position: 'relative', display: 'flex' }}>
                      <ShoppingBagOutlined sx={{ fontSize: 23 }} />
                      <Box sx={{ position: 'absolute', top: -5, right: -7, width: 16, height: 16, borderRadius: '50%', bgcolor: accent, color: '#fff', fontSize: 10, fontWeight: 700, display: 'grid', placeItems: 'center' }}>0</Box>
                    </Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, ml: 0.75 }}>₹0</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );

      case 'hero': {
        if (s.variant === 'tiles') {
          // Hero made of promo tiles: one large offer beside two stacked ones (Organic).
          const [main, ...side] = s.tiles;
          const tile = (t, i, big) => (
            <Box key={t.title} sx={{
              borderRadius: 5, overflow: 'hidden', position: 'relative', display: 'grid', gridTemplateColumns: big ? { xs: '1fr', md: '1.1fr 1fr' } : '1.3fr 1fr',
              alignItems: 'center', minHeight: big ? { xs: 360, md: 520 } : 250,
              background: i === 1 ? `linear-gradient(120deg, #fdf1d8 0%, ${bg} 100%)` : `linear-gradient(120deg, ${accent}2e 0%, ${bg} 80%)`,
            }}>
              <Box sx={{ p: big ? { xs: 4, md: 7 } : 3.5 }}>
                <Typography sx={{ ...eyebrow, fontSize: big ? 15 : 12 }}>{t.eyebrow}</Typography>
                <Typography {...(big ? { component: 'h1' } : {})} sx={{ ...h2, fontSize: big ? { xs: 36, md: 58 } : { xs: 22, md: 26 }, mb: big ? 2 : 1.5, textTransform: 'capitalize' }}>{t.title}</Typography>
                {big && <Typography sx={{ opacity: 0.7, mb: 3.5, maxWidth: 400 }}>{t.text}</Typography>}
                <Button {...action('#products')} size={big ? 'medium' : 'small'} sx={big ? solid : { ...solid, py: 0.75, px: 2.25, fontSize: 13 }}>{t.cta}</Button>
              </Box>
              <Art i={i + 2} height={big ? 380 : 170} icon={t.icon} sx={{ borderRadius: '50%', aspectRatio: '1', height: 'auto', width: '82%', mx: 'auto', display: big ? { xs: 'none', md: 'grid' } : 'grid' }} />
            </Box>
          );
          return (
            <Box key={key} component="section" id={sid('home')} sx={{ ...section, pt: { xs: 3, md: 4 }, pb: { xs: 4, md: 5 } }}>
              <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.7fr 1fr' }, gap: 3 }}>
                {tile(main, 0, true)}
                <Box sx={{ display: 'grid', gap: 3 }}>{side.map((t, i) => tile(t, i + 1, false))}</Box>
              </Box>
            </Box>
          );
        }
        if (s.variant === 'slider') {
          // Big banner slide plus three promo tiles beneath it (Organic).
          return (
            <Box key={key} component="section" id={sid('home')} sx={{ ...section, pt: { xs: 3, md: 4 }, pb: { xs: 4, md: 5 } }}>
              <Box sx={{ ...inner, borderRadius: 5, overflow: 'hidden', position: 'relative', minHeight: { xs: 380, md: 460 }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, alignItems: 'center', background: `linear-gradient(110deg, ${accent}2e 0%, ${bg} 70%)` }}>
                <Box sx={{ p: { xs: 4, md: 7 } }}>
                  <Typography sx={eyebrow}>{s.eyebrow}</Typography>
                  <Typography component="h1" sx={{ ...h2, fontSize: { xs: 36, md: 58 }, mb: 2 }}>{s.title}</Typography>
                  <Typography sx={{ opacity: 0.7, mb: 3.5, maxWidth: 420 }}>{s.text}</Typography>
                  <Button {...action('#products')} sx={solid}>{s.cta}</Button>
                  <Box sx={{ display: 'flex', gap: 1, mt: 4 }}>
                    {[0, 1, 2].map(d => <Box key={d} sx={{ width: d ? 10 : 28, height: 10, borderRadius: 999, bgcolor: d ? `${primary}26` : accent }} />)}
                  </Box>
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'block' }, p: 4 }}><Art i={2} height={380} sx={{ borderRadius: '50%', aspectRatio: '1', height: 'auto', width: '100%', maxWidth: 400, mx: 'auto' }} /></Box>
              </Box>
            </Box>
          );
        }
        if (s.variant === 'personal') {
          // Personal brand intro with a collage of photos (Stylist).
          return (
            <Box key={key} component="section" id={sid('home')} sx={{ ...section, pt: { xs: 5, md: 8 } }}>
              <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' }, gap: { xs: 5, md: 8 }, alignItems: 'center' }}>
                <Box>
                  <Typography sx={eyebrow}>{s.eyebrow}</Typography>
                  <Typography component="h1" sx={{ ...h2, fontSize: { xs: 40, md: 64 }, mb: 1.5, fontStyle: 'italic' }}>{s.title}</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 600, mb: 2, fontFamily: '"Poppins", sans-serif' }}>{s.name}</Typography>
                  <Typography sx={{ opacity: 0.7, mb: 4, maxWidth: 440, lineHeight: 1.8 }}>{s.text}</Typography>
                  <Button {...action('#about')} sx={solid}>{s.cta}</Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gridTemplateRows: '1fr 1fr', gap: 2, height: { xs: 360, md: 500 } }}>
                  <Art i={1} height="100%" sx={{ gridRow: 'span 2' }} />
                  <Art i={3} height="100%" icon="sparkle" />
                  <Art i={5} height="100%" icon="brush" dark />
                </Box>
              </Box>
            </Box>
          );
        }
        // Split hero: copy on the left, featured product on the right.
        return (
          <Box key={key} component="section" id={sid('home')} sx={{ ...section, pt: { xs: 5, md: 8 }, background: s.dark ? `linear-gradient(160deg, ${primary} 0%, ${primary}ee 100%)` : undefined, color: s.dark ? '#fff' : undefined }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: { xs: 5, md: 8 }, alignItems: 'center' }}>
              <Box>
                {s.bigBadge && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: { xs: 64, md: 96 }, lineHeight: 1, color: accent }}>{s.bigBadge}</Typography>
                    <Typography sx={{ fontSize: 16, fontWeight: 500, maxWidth: 160, lineHeight: 1.4, fontFamily: '"Poppins", sans-serif' }}>{s.bigBadgeText}</Typography>
                  </Box>
                )}
                {s.badge && (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: `${accent}22`, color: accent, px: 1.75, py: 0.6, borderRadius: 999, fontSize: 13, fontWeight: 600, mb: 2.5, fontFamily: '"Poppins", sans-serif' }}>
                    <LocalOfferRounded sx={{ fontSize: 16 }} />{s.badge}
                  </Box>
                )}
                <Typography sx={eyebrow}>{s.eyebrow}</Typography>
                <Typography component="h1" sx={{ ...h2, fontSize: { xs: 38, md: 62 }, lineHeight: 1.05, mb: 2.5 }}>{s.title}</Typography>
                <Typography sx={{ opacity: 0.72, mb: 3, maxWidth: 470, fontSize: 17, lineHeight: 1.7 }}>{s.text}</Typography>
                {s.price && <Box sx={{ mb: 3, fontSize: 22 }}><Price price={s.price} was={s.was} /></Box>}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button {...action('#products')} sx={solid}>{s.cta}</Button>
                  {s.scrollHint && (
                    <Typography {...link('products')} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 2, color: 'inherit', textDecoration: 'none', fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.6, fontFamily: '"Poppins", sans-serif' }}>
                      <Box sx={{ width: 22, height: 34, border: '2px solid currentColor', borderRadius: 999, position: 'relative', '&::after': { content: '""', position: 'absolute', left: '50%', top: 6, width: 3, height: 7, ml: '-1.5px', borderRadius: 2, bgcolor: 'currentColor' } }} />
                      Scroll down
                    </Typography>
                  )}
                  {s.cta2 && (
                    <Button {...action()} startIcon={s.video ? <PlayCircleOutlineRounded /> : undefined} sx={s.video ? { color: 'inherit', px: 2 } : ghost}>
                      {s.cta2}
                    </Button>
                  )}
                </Box>
              </Box>
              <Box sx={{ position: 'relative' }}>
                <Art i={0} height={s.tall ? 500 : 420} dark={s.dark} sx={s.arch ? { borderRadius: '240px 240px 24px 24px' } : undefined} />
                {s.product && (
                  <Box sx={{ position: 'absolute', left: { xs: 16, md: -28 }, bottom: 28, bgcolor: '#fff', color: primary, borderRadius: 3, px: 2.5, py: 1.75, boxShadow: `0 16px 40px ${primary}26`, fontFamily: '"Poppins", sans-serif' }}>
                    <Typography sx={{ fontSize: 12, opacity: 0.55 }}>Featured</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{s.product.name}</Typography>
                    <Typography sx={{ color: accent, fontWeight: 700, fontSize: 14 }}>{s.product.price}</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );
      }

      case 'usp':
        return (
          <Box key={key} sx={{ px: { xs: 3, md: 8 }, py: { xs: 4, md: 5 }, bgcolor: s.dark ? primary : '#fff', color: s.dark ? '#fff' : primary, fontFamily: '"Poppins", sans-serif' }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
              {s.items.map(([icon, title, text]) => {
                const Icon = ICONS[icon];
                return (
                  <Box key={title} sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <Box sx={{ width: 50, height: 50, flexShrink: 0, borderRadius: '50%', bgcolor: `${accent}1f`, display: 'grid', placeItems: 'center' }}>
                      <Icon sx={{ color: accent, fontSize: 25 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
                      <Typography sx={{ fontSize: 13, opacity: 0.6 }}>{text}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        );

      case 'categories':
        // Round category chips with icons (Plant).
        return (
          <Box key={key} component="section" sx={{ ...section, pb: 0 }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${s.items.length}, 1fr)` }, gap: 3 }}>
              {s.items.map(([icon, label], i) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#fff', borderRadius: 999, p: 1, pr: 3, boxShadow: `0 6px 22px ${primary}0d` }}>
                  <Art i={i} height={64} icon={icon} sx={{ width: 64, borderRadius: '50%', flexShrink: 0 }} />
                  <Typography sx={{ fontWeight: 600, fontFamily: font, textTransform: 'capitalize' }}>{label}</Typography>
                  <ArrowForwardRounded sx={{ ml: 'auto', color: accent }} />
                </Box>
              ))}
            </Box>
          </Box>
        );

      case 'products':
        return (
          <Box key={key} component="section" id={sid(s.id)} sx={{ ...section, bgcolor: s.white ? '#fff' : undefined }}>
            <Box sx={inner}>
              <Heading s={s} />
              {s.tabs && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: -2, mb: 4, fontFamily: '"Poppins", sans-serif' }}>
                  {s.tabs.map((t, i) => (
                    <Box key={t} sx={{ px: 2.25, py: 0.85, borderRadius: 999, fontSize: 13.5, fontWeight: 500, bgcolor: i ? 'transparent' : primary, color: i ? 'inherit' : '#fff', border: `1px solid ${i ? `${primary}22` : primary}` }}>{t}</Box>
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${s.columns || 4}, 1fr)` }, gap: 3 }}>
                {s.items.map((item, i) => <ProductCard key={item.name} item={item} i={i + idx} tall={s.columns === 3} />)}
              </Box>
              {s.more && (
                <Box sx={{ textAlign: 'center', mt: 5 }}>
                  <Button {...action()} endIcon={<ArrowForwardRounded />} sx={{ ...ghost, color: primary }}>{s.more}</Button>
                </Box>
              )}
            </Box>
          </Box>
        );

      case 'promos':
        // Two or three promo tiles side by side.
        return (
          <Box key={key} component="section" sx={{ ...section, py: { xs: 3, md: 4 } }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${s.items.length}, 1fr)` }, gap: 3 }}>
              {s.items.map((p, i) => (
                <Box key={p.title} sx={{
                  position: 'relative', borderRadius: shop.round === 0 ? 0 : 5, overflow: 'hidden', minHeight: s.items.length > 2 ? 220 : 260,
                  display: 'grid', gridTemplateColumns: '1.2fr 1fr', alignItems: 'center',
                  bgcolor: i % 2 ? `${accent}22` : `${primary}0d`,
                }}>
                  <Box sx={{ p: { xs: 3, md: 4 } }}>
                    {p.eyebrow && <Typography sx={{ ...eyebrow, fontSize: 12 }}>{p.eyebrow}</Typography>}
                    <Typography sx={{ ...h2, fontSize: { xs: 22, md: 28 }, mb: 1, textTransform: 'capitalize' }}>{p.title}</Typography>
                    {p.text && <Typography sx={{ fontSize: 14, opacity: 0.65, mb: 2 }}>{p.text}</Typography>}
                    <Typography {...(preview ? { component: 'span' } : { component: 'a', href: '#products' })} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600, fontSize: 14, color: primary, textDecoration: 'none', borderBottom: `2px solid ${accent}`, fontFamily: '"Poppins", sans-serif' }}>
                      {p.cta || 'Shop now'} <ArrowForwardRounded sx={{ fontSize: 16 }} />
                    </Typography>
                  </Box>
                  <Art i={i + 3} height="100%" icon={p.icon} sx={{ borderRadius: 0, minHeight: 'inherit', background: 'transparent' }} />
                </Box>
              ))}
            </Box>
          </Box>
        );

      case 'banner':
        return (
          <Box key={key} component="section" sx={{ ...section, py: { xs: 3, md: 4 } }}>
            <Box sx={{
              ...inner, borderRadius: shop.round === 0 ? 0 : 5, overflow: 'hidden', color: '#fff',
              background: `linear-gradient(115deg, ${primary} 0%, ${primary}f0 45%, ${accent} 140%)`,
              display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.3fr 1fr' }, alignItems: 'center',
            }}>
              <Box sx={{ p: { xs: 4, md: 7 } }}>
                <Typography sx={{ ...eyebrow, color: '#fff', opacity: 0.8 }}>{s.eyebrow}</Typography>
                <Typography sx={{ ...h2, mb: 1.5 }}>{s.title}</Typography>
                {s.text && <Typography sx={{ opacity: 0.75, mb: 3, maxWidth: 440 }}>{s.text}</Typography>}
                <Button {...action('#products')} sx={{ ...solid, bgcolor: '#fff', color: primary, '&:hover': { bgcolor: '#fff', opacity: 0.9 } }}>{s.cta}</Button>
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' }, p: 3 }}><Art i={4} height={260} icon={s.icon} dark /></Box>
            </Box>
          </Box>
        );

      case 'marquee':
        // Row of numbered sale cards (Jewelry).
        return (
          <Box key={key} sx={{ bgcolor: primary, color: '#fff', px: { xs: 3, md: 8 }, py: { xs: 3, md: 4 }, overflow: 'hidden', fontFamily: '"Poppins", sans-serif' }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 2 }}>
              {Array.from({ length: 6 }, (_, i) => (
                <Box key={i} sx={{ border: '1px solid #ffffff26', borderRadius: shop.round === 0 ? 0 : 3, p: 2, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: font, fontSize: 28, fontWeight: 700, color: accent, lineHeight: 1 }}>{String(i + 1).padStart(2, '0')}</Typography>
                  <Typography sx={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.7, mt: 1 }}>{s.label}</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{s.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );

      case 'deal':
        // Single product with a big image and a countdown (Organic).
        return (
          <Box key={key} component="section" sx={section}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 4, md: 8 }, alignItems: 'center' }}>
              <Art i={6} height={380} />
              <Box>
                <Typography sx={eyebrow}>{s.eyebrow}</Typography>
                <Typography sx={{ ...h2, mb: 1.5 }}>{s.title}</Typography>
                <Rating value={5} readOnly size="small" sx={{ mb: 1.5, color: accent }} />
                <Typography sx={{ opacity: 0.7, mb: 2.5, maxWidth: 440 }}>{s.text}</Typography>
                <Box sx={{ mb: 3, fontSize: 22 }}><Price price={s.price} was={s.was} /></Box>
                <Countdown days={s.days} preview={preview} accent={accent} primary={primary} />
                <Button {...action()} sx={{ ...solid, mt: 3.5 }}>{s.cta}</Button>
              </Box>
            </Box>
          </Box>
        );

      case 'testimonials':
        return (
          <Box key={key} component="section" sx={{ ...section, bgcolor: '#fff' }}>
            <Box sx={inner}>
              <Heading s={s} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${s.items.length}, 1fr)` }, gap: 3 }}>
                {s.items.map(([name, role, quote], i) => (
                  <Box key={name} sx={{ bgcolor: bg, borderRadius: shop.round === 0 ? 0 : 4, p: 3.5 }}>
                    <FormatQuoteRounded sx={{ color: accent, fontSize: 34 }} />
                    <Typography sx={{ opacity: 0.8, mb: 2.5, lineHeight: 1.75 }}>{quote}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, fontFamily: '"Poppins", sans-serif' }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: i % 2 ? accent : primary, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700 }}>{name[0]}</Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14.5 }}>{name}</Typography>
                        <Typography sx={{ fontSize: 12.5, opacity: 0.55 }}>{role}</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'blog':
        return (
          <Box key={key} component="section" id={sid('blog')} sx={section}>
            <Box sx={inner}>
              <Heading s={s} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                {s.items.map(([tag, title, date], i) => (
                  <Box key={title}>
                    <Art i={i + 2} height={220} />
                    <Typography sx={{ fontSize: 12.5, color: accent, fontWeight: 600, mt: 2, mb: 0.75, fontFamily: '"Poppins", sans-serif' }}>{tag} · {date}</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 18, fontFamily: font, mb: 1 }}>{title}</Typography>
                    <Typography {...(preview ? { component: 'span' } : { component: 'a', href: '#blog' })} sx={{ fontSize: 14, fontWeight: 600, color: primary, fontFamily: '"Poppins", sans-serif' }}>Read more</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'brands':
        return (
          <Box key={key} component="section" sx={{ ...section, py: { xs: 5, md: 6 }, bgcolor: '#fff' }}>
            <Box sx={inner}>
              <Typography sx={{ ...eyebrow, textAlign: 'center', mb: 3 }}>{s.title}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: `repeat(${s.items.length}, 1fr)` }, gap: 3 }}>
                {s.items.map(b => (
                  <Box key={b} sx={{ textAlign: 'center', fontWeight: 800, fontSize: 24, letterSpacing: 1, opacity: 0.35, fontFamily: '"Poppins", sans-serif' }}>{b}</Box>
                ))}
              </Box>
            </Box>
          </Box>
        );

      case 'about':
        return (
          <Box key={key} component="section" id={sid('about')} sx={{ ...section, bgcolor: '#fff' }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 5, md: 8 }, alignItems: 'center' }}>
              <Box sx={{ position: 'relative' }}>
                <Art i={2} height={440} />
                <Box sx={{ position: 'absolute', right: { xs: 16, md: -24 }, bottom: 32, bgcolor: accent, color: '#fff', borderRadius: 3, px: 3, py: 2.25, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.85, fontFamily: '"Poppins", sans-serif' }}>{s.badgeLabel}</Typography>
                  <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: 34 }}>{s.badge}</Typography>
                </Box>
              </Box>
              <Box>
                <Typography sx={eyebrow}>{s.eyebrow}</Typography>
                <Typography component="h2" sx={{ ...h2, mb: 2.5 }}>{s.title}</Typography>
                <Typography sx={{ opacity: 0.72, lineHeight: 1.85, mb: 3.5 }}>{s.text}</Typography>
                <Button {...action()} sx={solid}>{s.cta}</Button>
              </Box>
            </Box>
          </Box>
        );

      case 'services':
        return (
          <Box key={key} component="section" id={sid('services')} sx={section}>
            <Box sx={inner}>
              <Heading s={s} />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                {s.items.map(([icon, title, text]) => {
                  const Icon = ICONS[icon];
                  return (
                    <Box key={title} sx={{ bgcolor: '#fff', p: 4, textAlign: 'center', borderRadius: 4, boxShadow: `0 10px 30px ${primary}0d` }}>
                      <Box sx={{ width: 68, height: 68, mx: 'auto', mb: 2.5, borderRadius: '50%', bgcolor: `${accent}22`, display: 'grid', placeItems: 'center' }}>
                        <Icon sx={{ color: accent, fontSize: 32 }} />
                      </Box>
                      <Typography sx={{ fontFamily: font, fontWeight: 700, fontSize: 21, mb: 1 }}>{title}</Typography>
                      <Typography sx={{ opacity: 0.65, lineHeight: 1.7 }}>{text}</Typography>
                    </Box>
                  );
                })}
              </Box>
              {s.cta && <Box sx={{ textAlign: 'center', mt: 5 }}><Button {...action()} sx={{ ...ghost, color: primary }}>{s.cta}</Button></Box>}
            </Box>
          </Box>
        );

      case 'cta':
        // Full-bleed closing band (Stylist).
        return (
          <Box key={key} component="section" id={sid('contact')} sx={{ ...section, color: '#fff', textAlign: 'center', background: `linear-gradient(135deg, ${primary} 0%, ${primary}e6 55%, ${accent} 150%)` }}>
            <Box sx={{ ...inner, maxWidth: s.photo ? 1180 : 720, display: s.photo ? 'grid' : 'block', gridTemplateColumns: { md: '1fr 1.2fr' }, gap: 6, alignItems: 'center', textAlign: s.photo ? { xs: 'center', md: 'left' } : 'center' }}>
              {s.photo && <Art i={7} height={360} dark sx={{ display: { xs: 'none', md: 'grid' } }} />}
              <Box>
              <Typography sx={{ ...eyebrow, color: '#fff', opacity: 0.75 }}>{s.eyebrow}</Typography>
              <Typography sx={{ ...h2, fontSize: { xs: 32, md: 48 }, mb: 3.5, fontStyle: shop.serif ? 'italic' : 'normal' }}>{s.title}</Typography>
              <Button {...action()} sx={{ ...solid, bgcolor: '#fff', color: primary, '&:hover': { bgcolor: '#fff', opacity: 0.9 } }}>{s.cta}</Button>
              </Box>
            </Box>
          </Box>
        );

      case 'newsletter':
        return (
          <Box key={key} component="section" id={sid(s.id)} sx={{ ...section }}>
            <Box sx={{
              ...inner, borderRadius: shop.round === 0 ? 0 : 6, px: { xs: 3, md: 6 }, py: { xs: 5, md: 7 }, textAlign: 'center', position: 'relative', overflow: 'hidden',
              bgcolor: `${accent}1c`,
            }}>
              {s.sideArt && (
                <>
                  <Art i={1} height={200} sx={{ position: 'absolute', left: -40, top: '50%', transform: 'translateY(-50%)', width: 200, borderRadius: '50%', display: { xs: 'none', md: 'grid' } }} />
                  <Art i={4} height={200} sx={{ position: 'absolute', right: -40, top: '50%', transform: 'translateY(-50%)', width: 200, borderRadius: '50%', display: { xs: 'none', md: 'grid' } }} />
                </>
              )}
              <MailOutlineRounded sx={{ color: accent, fontSize: 40, mb: 1 }} />
              <Typography component="h2" sx={{ ...h2, mb: 1.25 }}>{s.title}</Typography>
              <Typography sx={{ opacity: 0.68, mb: 3.5 }}>{s.text}</Typography>
              <Newsletter s={s} />
            </Box>
          </Box>
        );

      case 'footer':
        return (
          <Box key={key} component="footer" id={sid('contact-info')} sx={{ bgcolor: primary, color: '#fff', px: { xs: 3, md: 8 }, pt: { xs: 6, md: 8 }, pb: 3, fontFamily: '"Poppins", sans-serif' }}>
            <Box sx={{ ...inner, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: `1.4fr repeat(${s.columns.length}, 1fr)` }, gap: 4 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MainIcon sx={{ color: accent }} />
                  <Typography sx={{ fontWeight: 800, fontSize: 20, fontFamily: font }}>{shop.brand}</Typography>
                </Box>
                <Typography sx={{ opacity: 0.65, fontSize: 14, lineHeight: 1.8, mb: 2.5, maxWidth: 300 }}>{s.about}</Typography>
                <Box sx={{ display: 'flex', gap: 1.25 }}>
                  {SOCIALS.slice(0, 4).map(([Icon, label]) => (
                    <Box key={label} aria-label={label} sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: '#ffffff14', display: 'grid', placeItems: 'center' }}>
                      <Icon sx={{ fontSize: 17 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
              {s.columns.map(([title, links]) => (
                <Box key={title}>
                  <Typography sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
                  {title === 'Newsletter'
                    ? <Newsletter s={{ cta: 'Join', center: false }} dark />
                    : links.map(l => <Typography key={l} sx={{ opacity: 0.65, fontSize: 14, mb: 1.1 }}>{l}</Typography>)}
                </Box>
              ))}
            </Box>
            <Box sx={{ ...inner, borderTop: '1px solid #ffffff1a', mt: 5, pt: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, fontSize: 13, opacity: 0.55 }}>
              <span>© {new Date().getFullYear()} {shop.brand}. All rights reserved.</span>
              <span>Privacy policy · Terms of service</span>
            </Box>
          </Box>
        );

      case 'comingSoon':
        return (
          <Box key={key} component="section" id={sid('home')} sx={{
            minHeight: preview ? 920 : '100vh', color: '#fff', display: 'flex', flexDirection: 'column', fontFamily: '"Poppins", sans-serif',
            background: `radial-gradient(1200px 600px at 80% -10%, ${accent}55 0%, transparent 60%), radial-gradient(900px 500px at -10% 110%, ${accent}33 0%, transparent 60%), ${primary}`,
          }}>
            <Box sx={{ px: { xs: 3, md: 8 }, py: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <MainIcon sx={{ color: accent }} />
              <Typography sx={{ fontWeight: 800, fontSize: 20 }}>{shop.brand}</Typography>
            </Box>
            <Box sx={{ flex: 1, display: 'grid', placeItems: 'center', px: 3, py: 6 }}>
              <Box sx={{ textAlign: 'center', maxWidth: 680 }}>
                <Typography sx={{ ...eyebrow, color: accent }}>{s.eyebrow}</Typography>
                <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 44, md: 76 }, lineHeight: 1.02, letterSpacing: '-0.03em', mb: 2.5 }}>{s.title}</Typography>
                <Typography sx={{ opacity: 0.7, fontSize: 17, mb: 5, lineHeight: 1.8 }}>{s.text}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
                  <Countdown days={s.days} preview={preview} accent={accent} primary={primary} dark />
                </Box>
                <Newsletter s={{ cta: 'Notify me' }} dark />
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mt: 5 }}>
                  {SOCIALS.slice(0, 4).map(([Icon, label]) => (
                    <Box key={label} aria-label={label} sx={{ width: 40, height: 40, borderRadius: '50%', border: '1px solid #ffffff33', display: 'grid', placeItems: 'center' }}>
                      <Icon sx={{ fontSize: 18 }} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
            <Typography sx={{ textAlign: 'center', fontSize: 13, opacity: 0.5, pb: 3 }}>© {new Date().getFullYear()} {shop.brand}</Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ bgcolor: bg, color: primary, fontFamily: font, minWidth: 0 }}>
      {shop.sections.map(render)}
    </Box>
  );
};

// Days / hours / minutes / seconds until a launch date a set number of days
// out. Thumbnails show a still value so the gallery does not re-render.
const Countdown = ({ days = 12, preview, accent, primary, dark }) => {
  const [target] = useState(() => Date.now() + days * 86400000 + 5 * 3600000 + 27 * 60000);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (preview) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [preview]);
  const left = Math.max(0, target - now) / 1000;
  const parts = [['Days', left / 86400], ['Hours', (left / 3600) % 24], ['Minutes', (left / 60) % 60], ['Seconds', left % 60]];
  return (
    <Box role="timer" aria-label="Time left" sx={{ display: 'flex', gap: { xs: 1.25, md: 2 }, fontFamily: '"Poppins", sans-serif' }}>
      {parts.map(([label, v]) => (
        <Box key={label} sx={{
          width: { xs: 70, md: dark ? 104 : 80 }, py: dark ? 2.25 : 1.5, textAlign: 'center', borderRadius: 3,
          bgcolor: dark ? '#ffffff12' : '#fff', border: `1px solid ${dark ? '#ffffff22' : `${primary}14`}`,
        }}>
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 26, md: dark ? 40 : 28 }, color: dark ? '#fff' : primary, lineHeight: 1.1 }}>
            {String(Math.floor(v)).padStart(2, '0')}
          </Typography>
          <Typography sx={{ fontSize: 11.5, letterSpacing: 1, textTransform: 'uppercase', color: dark ? '#ffffffaa' : accent }}>{label}</Typography>
        </Box>
      ))}
    </Box>
  );
};

export default ShopSite;
